import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import ImageFrame from "@/components/ImageFrame";
import { Reveal, ChapterLabel } from "@/components/motion";
import { IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = ["Mig selv", "Mit barn"];

const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Guden%C3%A5vej%2060%2C%20Voervadsbro%2C%208660%20Skanderborg";

const INFO = [
  {
    title: "Besøg",
    lines: ["Gudenåvej 60, Voervadsbro", "8660 Skanderborg"],
    link: { label: "Åbn i Google Maps ↗", href: MAPS_URL },
  },
  {
    title: "Telefon",
    lines: ["93 93 50 06"],
    href: "tel:+4593935006",
    testId: "kontakt-phone-link",
    note: "Ring, eller brug formularen her på siden.",
  },
  {
    title: "E-mail",
    lines: ["info@centerforhjerneregulering.dk"],
    href: "mailto:info@centerforhjerneregulering.dk",
    testId: "kontakt-email-link",
    note: "Send en besked. Du får svar inden for 1 til 2 hverdage.",
  },
  {
    title: "Sessioner",
    lines: ["Kun efter aftale"],
    note: "Træning foregår efter forudgående aftale.",
  },
];

const inputCls =
  "w-full rounded-none border border-sand bg-white/70 px-4 py-3.5 text-sm text-charcoal placeholder:text-muted focus:border-charcoal focus:outline-none transition-colors duration-300";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-graphite";

const Kontakt = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    audience: "",
    childAge: "",
    message: "",
    referral: "",
  });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        audience: form.audience,
        child_age: form.audience === "Mit barn" ? form.childAge : "",
        message: form.message,
        referral: form.referral,
      });
      toast.success("Mange tak for din henvendelse. Du får svar inden for 1 til 2 hverdage.");
      setForm({ name: "", email: "", phone: "", audience: "", childAge: "", message: "", referral: "" });
    } catch (err) {
      toast.error("Noget gik galt. Prøv igen, eller ring på 93 93 50 06.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="page-kontakt">
      <PageHero
        testId="hero-kontakt"
        overline="Kontakt"
        title={["Book en", "indledende samtale."]}
      />

      <section data-testid="section-kontakt-omgivelser">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <ImageFrame
              src={IMAGES.gudenaa}
              alt="Gudenåen ved Center for Hjerneregulering med sol gennem trækronerne"
              caption="Fig. 01 — Centerets omgivelser · Gudenåen"
              testId="kontakt-gudenaa-img"
              ratio="aspect-[21/9]"
              tone="none"
              position="center 60%"
            />
          </Reveal>
          <Reveal delay={0.15} className="mt-10 max-w-2xl">
            <p className="font-serif text-2xl font-light leading-snug tracking-tight text-charcoal sm:text-3xl">
              Træning i rolige rammer direkte ved Gudenåen.
            </p>
          </Reveal>
          <Reveal delay={0.25} className="mt-6 max-w-2xl">
            <p className="text-lg font-light leading-relaxed text-graphite">
              En uforpligtende samtale om, hvad du eller dit barn oplever, og om neurofeedback kan være
              relevant. Udfyld formularen, så vender jeg tilbage inden for 1 til 2 hverdage.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-hairline">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
          <div className="lg:col-span-5">
            <Reveal>
              <ChapterLabel number="01" label="Find centret" />
            </Reveal>
            <div className="mt-10 space-y-0">
              {INFO.map((item) => (
                <Reveal key={item.title}>
                  <div className="border-t border-hairline py-6">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted">{item.title}</p>
                    <p className="mt-3 text-lg text-charcoal">
                      {item.lines.map((line) =>
                        item.href ? (
                          <a
                            key={line}
                            href={item.href}
                            data-testid={item.testId}
                            className="block transition-colors duration-300 hover:text-muted"
                          >
                            {line}
                          </a>
                        ) : (
                          <span key={line} className="block">
                            {line}
                          </span>
                        ),
                      )}
                    </p>
                    {item.note && <p className="mt-1 text-sm text-muted">{item.note}</p>}
                    {item.link && (
                      <a
                        href={item.link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="kontakt-google-maps-link"
                        className="mt-3 inline-block border-b border-sand pb-0.5 font-mono text-[11px] uppercase tracking-[0.2em] text-graphite transition-colors duration-300 hover:border-charcoal hover:text-charcoal"
                      >
                        {item.link.label}
                      </a>
                    )}
                  </div>
                </Reveal>
              ))}
              <div className="border-t border-hairline" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <ChapterLabel number="02" label="Send en besked" />
            </Reveal>
            <form
              data-testid="contact-booking-form"
              onSubmit={submit}
              className="mt-10 border border-sand bg-surface p-6 sm:p-10"
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className={labelCls}>
                    Navn *
                  </label>
                  <input
                    id="cf-name"
                    data-testid="contact-form-input-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Dit navn"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className={labelCls}>
                    E-mail *
                  </label>
                  <input
                    id="cf-email"
                    data-testid="contact-form-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="din@mail.dk"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="cf-phone" className={labelCls}>
                    Telefon *
                  </label>
                  <input
                    id="cf-phone"
                    data-testid="contact-form-input-phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+45 ..."
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="cf-audience" className={labelCls}>
                    Henvendelsen gælder *
                  </label>
                  <select
                    id="cf-audience"
                    data-testid="contact-form-select-audience"
                    required
                    value={form.audience}
                    onChange={update("audience")}
                    className={inputCls}
                  >
                    <option value="" disabled>
                      Vælg ...
                    </option>
                    {AUDIENCES.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>
                {form.audience === "Mit barn" && (
                  <div>
                    <label htmlFor="cf-child-age" className={labelCls}>
                      Barnets alder *
                    </label>
                    <input
                      id="cf-child-age"
                      data-testid="contact-form-input-child-age"
                      type="text"
                      required
                      value={form.childAge}
                      onChange={update("childAge")}
                      placeholder="Fx 9 år"
                      className={inputCls}
                    />
                  </div>
                )}
                <div className="sm:col-span-2">
                  <label htmlFor="cf-message" className={labelCls}>
                    Hvad ønsker du hjælp til? *
                  </label>
                  <textarea
                    id="cf-message"
                    data-testid="contact-form-textarea-message"
                    rows={5}
                    required
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Fortæl kort om det, du gerne vil have hjælp til, fx søvn, fokus, stress eller performance."
                    className={`${inputCls} resize-y`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-referral" className={labelCls}>
                    Hvordan hørte du om centret? (valgfri)
                  </label>
                  <input
                    id="cf-referral"
                    data-testid="contact-form-input-referral"
                    type="text"
                    value={form.referral}
                    onChange={update("referral")}
                    placeholder="Fx anbefaling, Google, sociale medier"
                    className={inputCls}
                  />
                </div>
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-btn"
                disabled={sending}
                className="mt-8 w-full border border-charcoal bg-charcoal px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-canvas transition-colors duration-300 hover:bg-transparent hover:text-charcoal disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {sending ? "Sender ..." : "Send henvendelse"}
              </button>
              <p className="mt-5 text-xs leading-relaxed text-muted">
                Dine oplysninger bruges kun til at besvare din henvendelse og behandles fortroligt. De
                videregives ikke til andre uden dit samtykke. Når du sender formularen, behandles
                oplysningerne af centrets hosting- og e-mailtjeneste alene med det formål at levere din
                besked.
              </p>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Kontakt;
